import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../context/API';
import { Merchant, AdminMerchantDomain } from '../types';

// API Response Interfaces
interface MerchantResponse {
  message?: string;
  status: number;
  data: Merchant[];
  success?: boolean;
}

interface SingleMerchantResponse {
  message?: string;
  status: number;
  data: Merchant;
  success?: boolean;
}

// Types
export type MerchantType = 'merchant' | 'manager' | 'staff' | 'merchant_manager' | 'merchant_staff';

export interface CreateMerchantPayload {
  name: string;
  username: string;
  email: string;
  phone: string;
  publicPhone?: string;
  address: string;
  password?: string;
  merchantId?: string;
  managerId?: string;
}

export interface UpdateMerchantPayload {
  id: string;
  name: string;
  username: string;
  email: string;
  phone: string;
  publicPhone?: string;
  address: string;
  role?: string;
}

interface MerchantsState {
  merchants: Merchant[];
  managers: Merchant[];
  staff: Merchant[];
  loading: {
    merchants: boolean;
    managers: boolean;
    staff: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
    unlock: boolean;
  };
  error: string | null;
  domain: {
    data: AdminMerchantDomain | null;
    loading: boolean;
    actionLoading: boolean;
    error: string | null;
  };
}

const initialState: MerchantsState = {
  merchants: [],
  managers: [],
  staff: [],
  loading: {
    merchants: false,
    managers: false,
    staff: false,
    create: false,
    update: false,
    delete: false,
    unlock: false,
  },
  error: null,
  domain: {
    data: null,
    loading: false,
    actionLoading: false,
    error: null,
  },
};

const MERCHANT_USERS_ENDPOINT = '/merchant-users';

// Async thunks
export const fetchMerchants = createAsyncThunk<Merchant[], void, { rejectValue: string }>(
  'merchants/fetchMerchants',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get<MerchantResponse>(MERCHANT_USERS_ENDPOINT, {
        params: { role: 'merchant' },
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      });
      return response.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch merchants'
      );
    }
  }
);

export const fetchManagers = createAsyncThunk<Merchant[], void, { rejectValue: string }>(
  'merchants/fetchManagers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get<MerchantResponse>(MERCHANT_USERS_ENDPOINT, {
        params: { role: 'manager' },
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      });
      return response.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch managers'
      );
    }
  }
);

export const fetchStaff = createAsyncThunk<Merchant[], void, { rejectValue: string }>(
  'merchants/fetchStaff',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get<MerchantResponse>(MERCHANT_USERS_ENDPOINT, {
        params: { role: 'staff' },
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      });
      return response.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch staff'
      );
    }
  }
);

interface CreateMerchantParams {
  type: MerchantType;
  payload: CreateMerchantPayload;
}

export const createMerchant = createAsyncThunk<
  Merchant,
  CreateMerchantParams,
  { rejectValue: string }
>('merchants/createMerchant', async ({ type, payload }, { rejectWithValue }) => {
  try {
    const body = { ...payload, role: type };
    const response = await API.post<SingleMerchantResponse>(MERCHANT_USERS_ENDPOINT, body, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });

    if (response.data.status === 200) {
      return response.data.data;
    } else {
      return rejectWithValue(response.data.message || 'Creation failed');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to create merchant'
    );
  }
});

interface UpdateMerchantParams {
  type: MerchantType;
  payload: UpdateMerchantPayload;
}

export const updateMerchant = createAsyncThunk<
  Merchant,
  UpdateMerchantParams,
  { rejectValue: string }
>('merchants/updateMerchant', async ({ type, payload }, { rejectWithValue }) => {
  try {
    const updatePayload: any = {
      name: payload.name,
      username: payload.username,
      email: payload.email,
      phone: payload.phone,
      publicPhone: payload.publicPhone,
      address: payload.address,
    };

    if (payload.role) {
      updatePayload.role = payload.role;
    }

    const response = await API.put<SingleMerchantResponse>(
      `${MERCHANT_USERS_ENDPOINT}/${payload.id}`,
      updatePayload,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      }
    );

    if (response.data.status === 200) {
      return response.data.data;
    } else {
      return rejectWithValue(response.data.message || 'Update failed');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to update merchant'
    );
  }
});

export type OTPType =
  | 'registration'
  | 'password_reset'
  | 'email_verification'
  | 'login_2fa'
  | 'phone_verification'
  | 'account_deletion';

interface UnlockMerchantOtpParams {
  contact: string;
  type?: OTPType;
}

interface UnlockOtpResponse {
  message?: string;
  status: number;
  data: { clearedFailedRecords: number };
  success?: boolean;
}

export const unlockMerchantOtp = createAsyncThunk<
  { clearedFailedRecords: number },
  UnlockMerchantOtpParams,
  { rejectValue: string }
>('merchants/unlockMerchantOtp', async ({ contact, type }, { rejectWithValue }) => {
  try {
    const response = await API.post<UnlockOtpResponse>(
      '/admin/otp/unlock',
      { contact, type },
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      }
    );

    if (response.data.status === 200) {
      return response.data.data;
    } else {
      return rejectWithValue(response.data.message || 'Unlock failed');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to unlock merchant'
    );
  }
});

interface DeleteMerchantParams {
  type: MerchantType;
  id: string;
}

export const deleteMerchant = createAsyncThunk<
  string,
  DeleteMerchantParams,
  { rejectValue: string }
>('merchants/deleteMerchant', async ({ type, id }, { rejectWithValue }) => {
  try {
    const response = await API.delete(`${MERCHANT_USERS_ENDPOINT}/${id}`, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });

    if (response.data.status === 200) {
      return id;
    } else {
      return rejectWithValue(response.data.message || 'Delete failed');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to delete merchant'
    );
  }
});

// Admin: fetch a specific merchant's custom domain
export const fetchMerchantDomain = createAsyncThunk<
  AdminMerchantDomain | null,
  string,
  { rejectValue: string }
>('merchants/fetchMerchantDomain', async (merchantId, { rejectWithValue }) => {
  try {
    const response = await API.get(`admin/merchants/${merchantId}/domain`);
    return response.data.data;
  } catch (error: any) {
    if (error.response?.status === 404) return null;
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch domain'
    );
  }
});

// Admin: approve a merchant's pending domain
export const approveMerchantDomain = createAsyncThunk<
  AdminMerchantDomain,
  string,
  { rejectValue: string }
>('merchants/approveMerchantDomain', async (merchantId, { rejectWithValue }) => {
  try {
    const response = await API.post(`admin/merchants/${merchantId}/domain/approve`);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to approve domain'
    );
  }
});

// Admin: reject a merchant's pending domain
export const rejectMerchantDomain = createAsyncThunk<
  AdminMerchantDomain,
  { merchantId: string; reason: string },
  { rejectValue: string }
>('merchants/rejectMerchantDomain', async ({ merchantId, reason }, { rejectWithValue }) => {
  try {
    const response = await API.post(`admin/merchants/${merchantId}/domain/reject`, { reason });
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to reject domain'
    );
  }
});

// Admin: remove a merchant's domain
export const deleteMerchantDomain = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>('merchants/deleteMerchantDomain', async (merchantId, { rejectWithValue }) => {
  try {
    await API.delete(`admin/merchants/${merchantId}/domain`);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to delete domain'
    );
  }
});

const merchantsSlice = createSlice({
  name: 'merchants',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMerchantDomain: (state) => {
      state.domain = initialState.domain;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch merchants
      .addCase(fetchMerchants.pending, (state) => {
        state.loading.merchants = true;
        state.error = null;
      })
      .addCase(fetchMerchants.fulfilled, (state, action) => {
        state.loading.merchants = false;
        state.merchants = action.payload;
      })
      .addCase(fetchMerchants.rejected, (state, action) => {
        state.loading.merchants = false;
        state.error = action.payload as string;
      })
      // Fetch managers
      .addCase(fetchManagers.pending, (state) => {
        state.loading.managers = true;
        state.error = null;
      })
      .addCase(fetchManagers.fulfilled, (state, action) => {
        state.loading.managers = false;
        state.managers = action.payload;
      })
      .addCase(fetchManagers.rejected, (state, action) => {
        state.loading.managers = false;
        state.error = action.payload as string;
      })
      // Fetch staff
      .addCase(fetchStaff.pending, (state) => {
        state.loading.staff = true;
        state.error = null;
      })
      .addCase(fetchStaff.fulfilled, (state, action) => {
        state.loading.staff = false;
        state.staff = action.payload;
      })
      .addCase(fetchStaff.rejected, (state, action) => {
        state.loading.staff = false;
        state.error = action.payload as string;
      })
      // Create merchant
      .addCase(createMerchant.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createMerchant.fulfilled, (state, action) => {
        state.loading.create = false;
        const type = action.meta.arg.type;
        if (type === 'merchant') {
          state.merchants.push(action.payload);
        } else if (type === 'manager' || type === 'merchant_manager') {
          state.managers.push(action.payload);
        } else {
          state.staff.push(action.payload);
        }
      })
      .addCase(createMerchant.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload as string;
      })
      // Update merchant
      .addCase(updateMerchant.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateMerchant.fulfilled, (state, action) => {
        state.loading.update = false;
        const type = action.meta.arg.type;
        const updatedMerchant = action.payload;
        if (type === 'merchant') {
          const index = state.merchants.findIndex((m) => m.id === updatedMerchant.id);
          if (index !== -1) state.merchants[index] = updatedMerchant;
        } else if (type === 'manager') {
          const index = state.managers.findIndex((m) => m.id === updatedMerchant.id);
          if (index !== -1) state.managers[index] = updatedMerchant;
        } else {
          const index = state.staff.findIndex((m) => m.id === updatedMerchant.id);
          if (index !== -1) state.staff[index] = updatedMerchant;
        }
      })
      .addCase(updateMerchant.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload as string;
      })
      // Delete merchant
      .addCase(deleteMerchant.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(deleteMerchant.fulfilled, (state, action) => {
        state.loading.delete = false;
        const type = action.meta.arg.type;
        const id = action.payload;
        if (type === 'merchant') {
          state.merchants = state.merchants.filter((m) => m.id !== id);
        } else if (type === 'manager') {
          state.managers = state.managers.filter((m) => m.id !== id);
        } else {
          state.staff = state.staff.filter((m) => m.id !== id);
        }
      })
      .addCase(deleteMerchant.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload as string;
      })
      // Unlock merchant OTP
      .addCase(unlockMerchantOtp.pending, (state) => {
        state.loading.unlock = true;
        state.error = null;
      })
      .addCase(unlockMerchantOtp.fulfilled, (state) => {
        state.loading.unlock = false;
      })
      .addCase(unlockMerchantOtp.rejected, (state, action) => {
        state.loading.unlock = false;
        state.error = action.payload as string;
      })
      // Fetch merchant domain (admin)
      .addCase(fetchMerchantDomain.pending, (state) => {
        state.domain.loading = true;
        state.domain.error = null;
      })
      .addCase(fetchMerchantDomain.fulfilled, (state, action) => {
        state.domain.loading = false;
        state.domain.data = action.payload;
      })
      .addCase(fetchMerchantDomain.rejected, (state, action) => {
        state.domain.loading = false;
        state.domain.error = action.payload || 'Failed to fetch domain';
      })
      // Approve merchant domain (admin)
      .addCase(approveMerchantDomain.pending, (state) => {
        state.domain.actionLoading = true;
        state.domain.error = null;
      })
      .addCase(approveMerchantDomain.fulfilled, (state, action) => {
        state.domain.actionLoading = false;
        state.domain.data = action.payload;
      })
      .addCase(approveMerchantDomain.rejected, (state, action) => {
        state.domain.actionLoading = false;
        state.domain.error = action.payload || 'Failed to approve domain';
      })
      // Reject merchant domain (admin)
      .addCase(rejectMerchantDomain.pending, (state) => {
        state.domain.actionLoading = true;
        state.domain.error = null;
      })
      .addCase(rejectMerchantDomain.fulfilled, (state, action) => {
        state.domain.actionLoading = false;
        state.domain.data = action.payload;
      })
      .addCase(rejectMerchantDomain.rejected, (state, action) => {
        state.domain.actionLoading = false;
        state.domain.error = action.payload || 'Failed to reject domain';
      })
      // Delete merchant domain (admin)
      .addCase(deleteMerchantDomain.pending, (state) => {
        state.domain.actionLoading = true;
        state.domain.error = null;
      })
      .addCase(deleteMerchantDomain.fulfilled, (state) => {
        state.domain.actionLoading = false;
        state.domain.data = null;
      })
      .addCase(deleteMerchantDomain.rejected, (state, action) => {
        state.domain.actionLoading = false;
        state.domain.error = action.payload || 'Failed to delete domain';
      });
  },
});

export const { clearError, clearMerchantDomain } = merchantsSlice.actions;
export default merchantsSlice.reducer;